/**
 * 面包屑导航配置
 * Requirements: 7.1, 7.4, 7.5
 */

/**
 * 路径标题映射
 * 用于将 URL 路径转换为面包屑显示标题
 */
export const PATH_TITLES: Record<string, string> = {
  // 仪表盘
  '/dashboard': '仪表盘',

  // 个人中心
  '/my': '个人中心',
  '/my/devices': '我的设备',
  '/settings/profile': '个人资料',
  '/settings/my-notifications': '通知偏好',

  // 组织管理
  '/organization': '组织架构图',
  '/hr/departments': '部门管理',
  '/hr/positions': '职位管理',
  '/pm/teams': '团队管理',

  // 人事管理
  '/hr': '人事管理',
  '/hr/employees': '员工列表',
  '/hr/leaves': '请假管理',
  '/hr/leaves/pending': '待审批请假',
  '/hr/leaves/statistics': '请假统计',
  '/hr/salaries': '薪资管理',
  '/hr/salaries/payslips': '工资条',
  '/hr/salaries/my-payslips': '我的工资条',

  // 项目管理
  '/pm': '项目管理',
  '/pm/projects': '项目列表',
  '/pm/issues': '任务列表',
  '/pm/my-tasks': '我的任务',
  '/pm/my-stage-tasks': '我的阶段任务',
  '/pm/my-workflow-tasks': '我的工作流任务',
  '/pm/timelogs': '工时记录',
  '/pm/workload-stats': '工作量统计',
  '/pm/workflow-analytics': '工作流分析',
  '/pm/automation-rules': '自动化规则',

  // 知识库
  '/knowledge-base': '知识库',
  '/knowledge-base/search': '知识搜索',

  // 财务管理
  '/finance': '财务管理',
  '/finance/accounts': '账户管理',
  '/finance/cash-flows': '现金流',
  '/finance/categories': '分类管理',
  '/finance/transfers': '转账记录',
  '/finance/reimbursements': '报销管理',
  '/finance/currencies': '货币管理',

  // 行政管理
  '/admin': '行政管理',
  '/admin/devices': '设备管理',

  // 系统设置
  '/settings': '系统设置',
  '/settings/config': '系统配置',
  '/settings/notifications': '通知设置',
  '/settings/audit-logs': '审计日志',

  // 通知
  '/notifications': '通知中心',
}

/**
 * 动态路由标题生成器
 * 用于处理带参数的路由（如 /pm/projects/:id）
 */
export function getDynamicRouteTitle(
  segment: string,
  fullPath: string,
  context?: { projectName?: string; issueName?: string; teamName?: string; employeeName?: string }
): string | null {
  // UUID 格式的 ID
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
    // 如果有上下文信息，使用具体名称
    if (fullPath.includes('/projects/') && context?.projectName) {
      return context.projectName
    }
    if (fullPath.includes('/issues/') && context?.issueName) {
      return context.issueName
    }
    if (fullPath.includes('/teams/') && context?.teamName) {
      return context.teamName
    }
    if (fullPath.includes('/employees/') && context?.employeeName) {
      return context.employeeName
    }

    // 默认标题
    if (fullPath.includes('/projects/')) { return '项目详情' }
    if (fullPath.includes('/issues/')) { return '任务详情' }
    if (fullPath.includes('/teams/')) { return '团队详情' }
    if (fullPath.includes('/employees/')) { return '员工详情' }
    return '详情'
  }

  // 特殊路由段
  if (segment === 'gantt') { return '甘特图' }
  if (segment === 'kanban') { return '看板' }
  if (segment === 'settings') { return '设置' }
  if (segment === 'members') { return '成员' }

  return null
}

/**
 * 根据路径生成面包屑项
 * Requirements: 7.1, 7.5
 */
export interface BreadcrumbItemConfig {
  label: string
  path?: string
}

export function generateBreadcrumbFromPath(
  pathname: string,
  context?: { projectName?: string; issueName?: string; teamName?: string; employeeName?: string }
): BreadcrumbItemConfig[] {
  const segments = pathname.split('/').filter(Boolean)
  const items: BreadcrumbItemConfig[] = []

  let currentPath = ''

  for (const segment of segments) {
    currentPath += `/${segment}`

    // 跳过认证路由前缀
    if (segment === '_authenticated') {
      continue
    }

    // 查找静态标题
    const title = PATH_TITLES[currentPath]

    if (title) {
      items.push({
        label: title,
        path: currentPath,
      })
    } else {
      // 尝试动态路由标题
      const dynamicTitle = getDynamicRouteTitle(segment, currentPath, context)
      if (dynamicTitle) {
        items.push({
          label: dynamicTitle,
          path: currentPath,
        })
      }
    }
  }

  return items
}

/**
 * 获取父级路径
 * 用于详情页返回列表页
 */
export function getParentPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length <= 1) { return null }

  // 移除最后一个段
  segments.pop()
  return '/' + segments.join('/')
}

/**
 * 判断是否为详情页
 */
export function isDetailPage(pathname: string): boolean {
  // UUID 格式的最后一段表示详情页
  const segments = pathname.split('/').filter(Boolean)
  const lastSegment = segments[segments.length - 1]
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(lastSegment)
}
